import camelcaseKeys from 'camelcase-keys';
import {
  ConnectionData,
  ConnectionUpdateResponse,
  CreateConnectionRequest,
  EnterpriseConnectionData,
  TestConnectionResult,
  ToggleConnectionResponse,
  UpdateConnectionDataRequest
} from '~/api/types/connectionData.ts';
import { getRequestConfig, withMakeRequest } from '~/api';
import Request from '~/api/Request.ts';
import { getSnakeCase } from '~/utils/commonUtils.ts';

const getConnectionList: (
  organizationId?: string
) => Promise<ConnectionData[]> = withMakeRequest(
  async (organizationId) => {
    const params = new URLSearchParams();
    params.set('include', 'all');
    if (organizationId) {
      params.set('organization_id', organizationId);
    }
    return await Request.getInstance().get(
      `/connections?${params.toString()}`,
      getRequestConfig({ addEnvDomain: true })
    );
  },
  (data) => data.connections
);

export type SearchConnectionsResponse = {
  connections: ConnectionData;
  totalSize: number;
  nextPageToken: string;
  prevPageToken: string;
};

const searchConnections: ({
  enabled
}: {
  enabled: boolean;
}) => Promise<SearchConnectionsResponse> = withMakeRequest(
  async ({ enabled }) =>
    Request.getInstance().get(
      `/organizations/-/connections:search?enabled=${enabled}`,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data) => data
);

const addSSOConnectionForOrganization: (
  organizationId: string,
  request: CreateConnectionRequest
) => Promise<ConnectionData> = withMakeRequest(
  async (
    organizationId: string,
    { flags: _, ...connection }: CreateConnectionRequest
  ) =>
    Request.getInstance().post(
      `/organizations/${organizationId}/connections`,
      connection,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data) => data.connection
);

const addConnectionForEnvironment: (
  request: CreateConnectionRequest
) => Promise<ConnectionData> = withMakeRequest(
  async ({ flags = {}, ...connection }: CreateConnectionRequest) => {
    const creationFlags = Object.keys(flags)
      .map((flag) => `flags.${getSnakeCase(flag)}=${String(flags[flag])}`)
      .join('&');
    return await Request.getInstance().post(
      `/connections${creationFlags ? `?${creationFlags}` : ''}`,
      connection,
      getRequestConfig({ addEnvDomain: true })
    );
  },
  (data) => data.connection
);

const getSSOConnectionDetails: (
  organizationId: string,
  connectionId: string
) => Promise<ConnectionData> = withMakeRequest(
  async (organizationId, connectionId) =>
    await Request.getInstance().get(
      `/organizations/${organizationId}/connections/${connectionId}`,
      getRequestConfig({ addEnvDomain: true, camelCaseResponse: false })
    ),
  ({ connection }) => {
    const { attribute_mapping: attributeMapping, ...connectionData } =
      connection;
    return {
      ...camelcaseKeys(connectionData, { deep: true }),
      attributeMapping
    } as ConnectionData;
  }
);

const getEnvironmentConnectionDetails: (
  connectionId: string
) => Promise<ConnectionData> = withMakeRequest(
  async (connectionId) =>
    await Request.getInstance().get(
      `/connections/${connectionId}`,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data) => data.connection
);

const updateConnection: (
  organizationId: string,
  connectionId: string,
  connection: UpdateConnectionDataRequest | ConnectionData
) => Promise<ConnectionData> = withMakeRequest(
  async (organizationId, connectionId, connection) =>
    await Request.getInstance().patch(
      `/organizations/${organizationId}/connections/${connectionId}`,
      connection,
      getRequestConfig({ addEnvDomain: true })
    ),
  ({ connection }: ConnectionUpdateResponse) => connection
);

const updateConnectionForEnvironment: (
  connectionId: string,
  connection: UpdateConnectionDataRequest | ConnectionData,
  keyId: string
) => Promise<ConnectionData> = withMakeRequest(
  async (connectionId, connection, keyId) =>
    await Request.getInstance().patch(
      `/connections/${connectionId}`,
      { ...connection, keyId },
      getRequestConfig({ addEnvDomain: true })
    ),
  ({ connection }: ConnectionUpdateResponse) => connection
);

const toggleConnection: (
  organizationId: string,
  connectionId: string,
  shouldEnable: boolean
) => Promise<boolean> = withMakeRequest(
  async (organizationId, connectionId, shouldEnable) =>
    await Request.getInstance().patch(
      `/organizations/${organizationId}/connections/${connectionId}:${shouldEnable ? 'enable' : 'disable'}`,
      {},
      getRequestConfig({ addEnvDomain: true })
    ),
  ({ enabled }: ToggleConnectionResponse) => enabled
);

const toggleEnvironmentConnection: (
  connectionId: string,
  shouldEnable: boolean
) => Promise<boolean> = withMakeRequest(
  async (connectionId, shouldEnable) =>
    await Request.getInstance().patch(
      `/connections/${connectionId}:${shouldEnable ? 'enable' : 'disable'}`,
      {},
      getRequestConfig({ addEnvDomain: true })
    ),
  ({ enabled }: ToggleConnectionResponse) => enabled
);

const deleteConnection: (
  organizationId: string,
  connectionId: string
) => Promise<boolean> = withMakeRequest(
  async (organizationId, connectionId) =>
    await Request.getInstance().delete(
      `/organizations/${organizationId}/connections/${connectionId}`,
      getRequestConfig({ addEnvDomain: true })
    ),
  () => true
);

const deleteEnvironmentConnection: (connectionId: string) => Promise<boolean> =
  withMakeRequest(
    async (connectionId) =>
      await Request.getInstance().delete(
        `/connections/${connectionId}`,
        getRequestConfig({ addEnvDomain: true })
      ),
    () => true
  );

const getTestConnectionResult: (
  connectionId: string,
  testRequestId: string
) => Promise<TestConnectionResult> = withMakeRequest(
  async (connectionId: string, testRequestId: string) =>
    await Request.getInstance().get(
      `/connections/${connectionId}/test-requests/${testRequestId}`,
      getRequestConfig({ addEnvDomain: true })
    )
);

export type EnvironmentSsoConnectionListRequest = {
  pageToken: string;
  pageSize: number;
};

export type EnvironmentSsoConnectionListResponse = {
  nextPageToken: string;
  totalSize: number;
  prevPageToken: string;
  connections: EnterpriseConnectionData[];
};

const getEnvSsoConnectionList: (
  request: EnvironmentSsoConnectionListRequest
) => Promise<EnvironmentSsoConnectionListResponse> = withMakeRequest(
  async ({ pageToken, pageSize }: EnvironmentSsoConnectionListRequest) =>
    await Request.getInstance().get(
      `/organizations/-/connections?page_token=${pageToken}&page_size=${pageSize}`,
      getRequestConfig({ addEnvDomain: true })
    )
);

export {
  getTestConnectionResult,
  deleteEnvironmentConnection,
  deleteConnection,
  toggleEnvironmentConnection,
  toggleConnection,
  updateConnectionForEnvironment,
  updateConnection,
  getEnvironmentConnectionDetails,
  getSSOConnectionDetails,
  addConnectionForEnvironment,
  addSSOConnectionForOrganization,
  getConnectionList,
  getEnvSsoConnectionList,
  searchConnections
};
